import {
  NEW_TUTOR_ONBOARDING_TOPIC,
  SAT_BOOTCAMP_ONBOARDING_TOPIC,
  SessionAttendeeRole,
} from '@/queries/constants'
import SeriesQ from '@/queries/SeriesQ'
import SessionAttendeeQ from '@/queries/SessionAttendeeQ'
import SessionHostQ from '@/queries/SessionHostQ'
import SessionQ from '@/queries/SessionQ'
import TopicQ from '@/queries/TopicQ'
import TrainingQ from '@/queries/TrainingQ'
import TrainingSubmissionQ from '@/queries/TrainingSubmissionQ'
import TutorAuditQ from '@/queries/TutorAuditQ'
import UserQ from '@/queries/UserQ'
import { SQL, type SQLFragment } from '@/client/query'

import { REQUIRED_TRAININGS_SLUGS } from '../training'
import { EXCLUDED_SESSION_TOPICS } from './constants'
import type { AutomaticallyLoggedVolunteerActivitiesType } from './types'

// Maps each type of automatically tracked volunteer activity to a query
// that retrieves the relevant entities. When it's convenient, we include
// the fields that are required to generate the Volunteer Activity entry
// for the relevant entity (as it's more efficient to fetch in a single query).
// However sometimes, we fetch additional properties in the generator itself,
// for example `generators.auditSession()`
export function automaticallyTrackedVolunteerActivityEntitiesRequiringSync({
  userId,
  activityType,
}: {
  userId: SQLFragment<number> | number
  activityType: AutomaticallyLoggedVolunteerActivitiesType
}): SQLFragment<unknown> {
  const queries: Record<AutomaticallyLoggedVolunteerActivitiesType, SQLFragment<unknown>> = {
    'host-session': SessionHostQ.where(
      {
        userId,
        approved: true,
        include: [
          {
            association: 'Session',
            required: true,
            include: [
              {
                association: 'Topic',
                required: true,
              },
              {
                association: 'World',
                required: false,
              },
            ],
          },
        ],
      },
      SQL`
        ${SessionQ.isEligibleForVolunteerHours()} AND
        ${SessionHostQ.session_host_volunteer_activity_id} IS NULL`
    ),
    'tutor-training': TrainingSubmissionQ.where(
      {
        userId,
        hasSubmitted: true,
        include: [
          {
            association: 'Training',
            required: true,
          },
        ],
      },
      SQL`
        (
          ${TrainingSubmissionQ.assignment_mentor_user_id} IS NOT NULL OR
          ${TrainingQ.slug} = ANY(${REQUIRED_TRAININGS_SLUGS})
        ) AND
        ${TrainingSubmissionQ.volunteer_activity_id} IS NULL
      `
    ),
    'onboarding-buddy-session': SessionAttendeeQ.where(
      {
        userId,
        roles: [SessionAttendeeRole.OnboardingBuddy],
        include: [
          {
            association: 'Session',
            include: [
              {
                association: 'Topic',
                where: SQL`(${TopicQ.name} != ALL(${EXCLUDED_SESSION_TOPICS}))`,
              },
            ],
          },
        ],
      },
      SQL`
        ${SessionAttendeeQ.volunteer_activity_id} IS NULL
      `
    ),
    'attend-new-tutor-onboarding-session': SQL`FROM (
      WITH most_recent_onboarding_session AS (
        SELECT ${SessionQ.id} AS "id"
        ${SessionAttendeeQ.where(
          {
            userId,
            include: [
              {
                association: 'Session',
                required: true,
                include: [
                  {
                    association: 'Topic',
                    required: true,
                    where: SQL`${TopicQ.name} = ${NEW_TUTOR_ONBOARDING_TOPIC}`,
                  },
                ],
              },
              { association: 'User', required: true },
            ],
            roles: [SessionAttendeeRole.Learner],
            // We only ever want to consider the tutor's first onboarding session
            order: [[SessionQ.start_date, 'asc']],
            limit: 1,
          },
          // Need to filter since users can attend onboarding sessions as learners.
          // It's not perfect because we don't know WHEN they became a tutor, so if
          // someone attends an onboarding session as a learner, then becomes a tutor,
          // they'll be able to claim volunteer hours for it.
          UserQ.hasTutorRole()
        )}
      )
      SELECT *
      ${SessionAttendeeQ.where(
        {
          userId,
          sessionId: SQL`(SELECT "id" FROM most_recent_onboarding_session)`,
        },
        SQL`${SessionAttendeeQ.volunteer_activity_id} IS NULL`
      )}
    ) AS ${SessionAttendeeQ}
    `,
    'attend-sat-bootcamp-onboarding-session': SQL`FROM (
      WITH first_sat_onboarding_session AS (
        SELECT ${SessionQ.id} AS "id"
        ${SessionAttendeeQ.where(
          {
            userId,
            include: [
              {
                association: 'Session',
                required: true,
                include: [
                  {
                    association: 'Topic',
                    required: true,
                    where: SQL`${TopicQ.name} = ${SAT_BOOTCAMP_ONBOARDING_TOPIC}`,
                  },
                ],
              },
              { association: 'User', required: true },
            ],
            roles: [SessionAttendeeRole.Learner],
            order: [[SessionQ.start_date, 'asc']],
            limit: 1,
          },
          SQL`EXISTS(SELECT 1 ${SessionQ.where(
            {
              attemptedHosting: true,
              tutor: userId,
              include: [
                {
                  association: 'Series',
                  required: true,
                  where: SQL`${SeriesQ.isSATSeries()}`,
                },
              ],
            },
            SQL`${SessionQ.start_date} > ${SessionAttendeeQ.joined_at}`
          )}) AND ${SessionAttendeeQ.volunteer_activity_id} IS NULL`
        )}
      )
      SELECT *
      ${SessionAttendeeQ.where(
        {
          userId,
          sessionId: SQL`(SELECT "id" FROM first_sat_onboarding_session)`,
        },
        SQL`${SessionAttendeeQ.volunteer_activity_id} IS NULL`
      )}
    ) AS ${SessionAttendeeQ}`,
    'session-audited': TutorAuditQ.where(
      {
        auditorId: userId,
      },
      SQL`${TutorAuditQ.isUsingAuditorRubricVersion()} AND
          ${TutorAuditQ.volunteer_activity_id} IS NULL`
    ),
  }

  return queries[activityType]
}

export function requiresVolunteerHoursSync({
  userId,
  activityType,
}: {
  userId: SQLFragment<number> | number
  activityType: AutomaticallyLoggedVolunteerActivitiesType
}): SQLFragment<boolean> {
  return SQL`
    EXISTS(SELECT 1 ${automaticallyTrackedVolunteerActivityEntitiesRequiringSync({
      userId,
      activityType,
    })})
    `
}
